import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone,
  Plus,
  Pencil,
  TrashSimple,
  Globe,
  Users,
  Clock,
  LinkSimple,
  FacebookLogo,
  InstagramLogo,
  TiktokLogo,
  TelegramLogo,
  WhatsappLogo,
  RedditLogo,
  DiscordLogo,
  YoutubeLogo,
  Envelope,
  Storefront,
  ChatCircle,
} from "@phosphor-icons/react";
import { useConfirm } from "@/hooks/useConfirm";

const PLATFORMS = [
  { key: "whatsapp_group",     label: "WhatsApp · Grupo",       icon: WhatsappLogo,  color: "#25D366" },
  { key: "whatsapp_broadcast", label: "WhatsApp · Difusión",    icon: WhatsappLogo,  color: "#128C7E" },
  { key: "telegram_group",     label: "Telegram · Grupo",       icon: TelegramLogo,  color: "#26A5E4" },
  { key: "telegram_channel",   label: "Telegram · Canal",       icon: TelegramLogo,  color: "#0088CC" },
  { key: "facebook_group",     label: "Facebook · Grupo",       icon: FacebookLogo,  color: "#1877F2" },
  { key: "facebook_marketplace", label: "Facebook Marketplace", icon: Storefront,    color: "#1877F2" },
  { key: "instagram",          label: "Instagram",              icon: InstagramLogo, color: "#E4405F" },
  { key: "tiktok",             label: "TikTok",                 icon: TiktokLogo,    color: "#000000" },
  { key: "twitter",            label: "X / Twitter",            icon: ChatCircle,    color: "#000000" },
  { key: "reddit",             label: "Reddit",                 icon: RedditLogo,    color: "#FF4500" },
  { key: "discord",            label: "Discord",                icon: DiscordLogo,   color: "#5865F2" },
  { key: "forum",              label: "Foro web",               icon: Globe,         color: "#71717A" },
  { key: "youtube",            label: "YouTube",                icon: YoutubeLogo,   color: "#FF0000" },
  { key: "email_list",         label: "Lista de email",         icon: Envelope,      color: "#3F51B5" },
  { key: "marketplace",        label: "Marketplace genérico",   icon: Storefront,    color: "#71717A" },
  { key: "other",              label: "Otro",                   icon: Megaphone,     color: "#71717A" },
];
const PLATFORM_BY_KEY = Object.fromEntries(PLATFORMS.map((p) => [p.key, p]));

const EMPTY = {
  nombre: "",
  plataforma: "facebook_group",
  link: "",
  tema_nicho: "",
  miembros_estimados: 0,
  frecuencia_max_semanal: 2,
  horario_recomendado: "",
  notas: "",
  activo: true,
};

function ChannelForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.nombre.trim()) {
      toast.error("Ponle un nombre al canal");
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/channels/${initial.id}`, form);
        toast.success("Canal actualizado");
      } else {
        await api.post("/channels", form);
        toast.success("Canal agregado");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 p-5 space-y-3" data-testid="channel-form">
      <h3 className="font-bold text-lg">{isEdit ? "Editar canal" : "Agregar canal"}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide font-bold text-zinc-600">Nombre</span>
          <input
            value={form.nombre}
            onChange={(e) => set("nombre", e.target.value)}
            placeholder="Ej. Vendedores Bicis CDMX"
            className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
            data-testid="ch-nombre"
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide font-bold text-zinc-600">Plataforma</span>
          <select
            value={form.plataforma}
            onChange={(e) => set("plataforma", e.target.value)}
            className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none bg-white"
            data-testid="ch-plataforma"
          >
            {PLATFORMS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] uppercase tracking-wide font-bold text-zinc-600">Link o invitación</span>
          <input
            value={form.link}
            onChange={(e) => set("link", e.target.value)}
            placeholder="https://chat.whatsapp.com/… o https://facebook.com/groups/…"
            className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none font-mono"
            data-testid="ch-link"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] uppercase tracking-wide font-bold text-zinc-600">Tema / nicho</span>
          <input
            value={form.tema_nicho}
            onChange={(e) => set("tema_nicho", e.target.value)}
            placeholder="Ej. bicis usadas, iPhones seminuevos, herramientas..."
            className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
            data-testid="ch-tema"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide font-bold text-zinc-600">Miembros estimados</span>
          <input
            type="number"
            min={0}
            value={form.miembros_estimados}
            onChange={(e) => set("miembros_estimados", parseInt(e.target.value) || 0)}
            className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
            data-testid="ch-miembros"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wide font-bold text-zinc-600">Posts máx. por semana</span>
          <input
            type="number"
            min={0}
            max={14}
            value={form.frecuencia_max_semanal}
            onChange={(e) => set("frecuencia_max_semanal", parseInt(e.target.value) || 0)}
            className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
            data-testid="ch-frecuencia"
          />
          <span className="text-[10px] text-zinc-500">Respetar el límite te protege de bans.</span>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] uppercase tracking-wide font-bold text-zinc-600">Horario recomendado</span>
          <input
            value={form.horario_recomendado}
            onChange={(e) => set("horario_recomendado", e.target.value)}
            placeholder="Ej. viernes 7pm, mañanas entre semana..."
            className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
            data-testid="ch-horario"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] uppercase tracking-wide font-bold text-zinc-600">Notas internas</span>
          <textarea
            value={form.notas}
            onChange={(e) => set("notas", e.target.value)}
            rows={2}
            placeholder="Quién es el admin, reglas del grupo, contacto, etc."
            className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 focus:border-klein focus:outline-none"
            data-testid="ch-notas"
          />
        </label>
        <label className="flex items-center gap-2 sm:col-span-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.activo}
            onChange={(e) => set("activo", e.target.checked)}
            className="accent-klein w-4 h-4"
            data-testid="ch-activo"
          />
          <span className="text-sm">Canal activo (lo incluye la IA al sugerir plan de publicación)</span>
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="px-4 py-2 text-sm font-bold bg-klein text-white hover:bg-klein-dark disabled:opacity-50"
          data-testid="ch-save"
        >
          {busy ? "Guardando…" : isEdit ? "Guardar cambios" : "Agregar canal"}
        </button>
      </div>
    </div>
  );
}

function ChannelCard({ ch, onEdit, onDelete }) {
  const plat = PLATFORM_BY_KEY[ch.plataforma] || PLATFORM_BY_KEY.other;
  const Icon = plat.icon;
  return (
    <div
      className={`bg-white border ${ch.activo ? "border-zinc-200" : "border-zinc-200 opacity-50"} p-4 flex flex-col gap-2`}
      data-testid={`channel-${ch.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={22} weight="duotone" style={{ color: plat.color }} className="shrink-0" />
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">{ch.nombre}</div>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-zinc-500">{plat.label}</div>
          </div>
        </div>
        {!ch.activo && (
          <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 bg-zinc-100 text-zinc-600">
            Pausado
          </span>
        )}
      </div>
      {ch.tema_nicho && (
        <p className="text-xs text-zinc-700 leading-snug" data-testid={`ch-tema-${ch.id}`}>
          🎯 {ch.tema_nicho}
        </p>
      )}
      <div className="flex flex-wrap gap-2 text-[11px] text-zinc-600">
        {ch.miembros_estimados > 0 && (
          <span className="inline-flex items-center gap-1">
            <Users size={11} /> {ch.miembros_estimados.toLocaleString()}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Megaphone size={11} /> máx {ch.frecuencia_max_semanal}/sem
        </span>
        {ch.horario_recomendado && (
          <span className="inline-flex items-center gap-1">
            <Clock size={11} /> {ch.horario_recomendado}
          </span>
        )}
        {ch.posts_count > 0 && (
          <span className="inline-flex items-center gap-1 text-klein font-semibold">
            {ch.posts_count} publicación(es)
          </span>
        )}
      </div>
      {ch.link && (
        <a
          href={ch.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-klein hover:underline inline-flex items-center gap-1 truncate"
          data-testid={`ch-link-${ch.id}`}
        >
          <LinkSimple size={11} /> Abrir
        </a>
      )}
      {ch.notas && <p className="text-[11px] text-zinc-500 italic leading-snug line-clamp-2">{ch.notas}</p>}
      <div className="flex gap-2 pt-1 border-t border-zinc-100 mt-auto">
        <button
          type="button"
          onClick={() => onEdit(ch)}
          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-zinc-300 hover:bg-zinc-50"
          data-testid={`ch-edit-${ch.id}`}
        >
          <Pencil size={10} /> Editar
        </button>
        <button
          type="button"
          onClick={() => onDelete(ch)}
          className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50"
          data-testid={`ch-delete-${ch.id}`}
        >
          <TrashSimple size={10} /> Borrar
        </button>
      </div>
    </div>
  );
}

export default function Canales() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterPlat, setFilterPlat] = useState("");
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/channels");
      setChannels(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los canales");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/channels");
        if (!cancelled) setChannels(data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "No se pudieron cargar los canales");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!filterPlat) return channels;
    return channels.filter((c) => c.plataforma === filterPlat);
  }, [channels, filterPlat]);

  const byPlatform = useMemo(() => {
    const out = {};
    for (const c of channels) {
      out[c.plataforma] = (out[c.plataforma] || 0) + 1;
    }
    return out;
  }, [channels]);

  const onSaved = () => {
    setShowForm(false);
    setEditing(null);
    load();
  };

  const onDelete = async (ch) => {
    const ok = await confirm({
      title: "¿Borrar canal?",
      description: `Esta acción no se puede deshacer. El historial de publicaciones de "${ch.nombre}" se conserva en métricas.`,
      confirmLabel: "Borrar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/channels/${ch.id}`);
      toast.success("Canal eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar");
    }
  };

  return (
    <section className="px-4 sm:px-6 md:px-10 py-8 max-w-6xl mx-auto" data-testid="canales-page">
      <header className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Megaphone size={28} weight="duotone" className="text-klein" />
            <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
              Canales
            </h1>
          </div>
          <p className="text-sm text-zinc-600 max-w-2xl">
            Tu directorio de grupos, canales y plataformas donde publicas manualmente.
            Conforme uses cada canal, las métricas te dirán cuáles convierten mejor — la base se vuelve tu activo más valioso.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-klein text-white text-sm font-bold hover:bg-klein-dark"
          data-testid="add-channel"
        >
          <Plus size={14} weight="bold" /> Agregar canal
        </button>
      </header>

      {/* Stats line */}
      <div className="mb-4 flex flex-wrap gap-3 text-xs text-zinc-600">
        <span className="font-bold text-ink" data-testid="total-channels">{channels.length}</span> canales en total
        {Object.entries(byPlatform).slice(0, 5).map(([plat, n]) => {
          const p = PLATFORM_BY_KEY[plat];
          if (!p) return null;
          return (
            <button
              key={plat}
              onClick={() => setFilterPlat(filterPlat === plat ? "" : plat)}
              className={`inline-flex items-center gap-1 px-2 py-0.5 border ${filterPlat === plat ? "border-klein text-klein" : "border-zinc-200 hover:bg-zinc-50"}`}
              data-testid={`filter-${plat}`}
            >
              <p.icon size={11} weight="bold" style={{ color: p.color }} /> {p.label.split(" · ")[0]}: {n}
            </button>
          );
        })}
        {filterPlat && (
          <button
            onClick={() => setFilterPlat("")}
            className="text-klein hover:underline"
          >
            Limpiar filtro
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-5">
          <ChannelForm
            initial={editing}
            onCancel={() => { setShowForm(false); setEditing(null); }}
            onSaved={onSaved}
          />
        </div>
      )}

      {loading ? (
        <div className="text-center text-sm text-zinc-500 py-12">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-zinc-50 border border-dashed border-zinc-300">
          <Megaphone size={36} weight="duotone" className="mx-auto text-zinc-400 mb-3" />
          <p className="text-sm text-zinc-600 mb-2">Aún no tienes canales registrados.</p>
          <p className="text-xs text-zinc-500 max-w-md mx-auto mb-4">
            Empieza con 3-5 grupos donde ya publicas o donde te interesa entrar.
            Si no sabes por dónde, abre un producto en Inventario y pulsa «Investigar audiencia» — la IA te sugerirá tipos de grupos para buscar.
          </p>
          <button
            type="button"
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-klein text-white text-sm font-bold hover:bg-klein-dark"
          >
            <Plus size={14} weight="bold" /> Agregar mi primer canal
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="channel-grid">
          {filtered.map((ch) => (
            <ChannelCard
              key={ch.id}
              ch={ch}
              onEdit={(c) => { setEditing(c); setShowForm(true); }}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {confirmDialog}
    </section>
  );
}